import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
	type BarryIndividualConversation,
	getIndividualConversation,
} from '~api/common/barry/barry';
import type { ApiError } from '~types/api';
import { getApiErrorText } from '~utils/getApiErrorText';
import { callErrorToast } from '~utils/toasts';

const barryConversationQueryKey = (conversationId: string): readonly unknown[] => [
	'getIndividualBarryConversation',
	conversationId,
];

export const useInvalidateIndividualConversation = (conversationId: string) => {
	const queryClient = useQueryClient();

	return () => queryClient.invalidateQueries(barryConversationQueryKey(conversationId));
};

export const useGetIndividualConversation = (
	conversationUuid: string,
	enabled: boolean,
	onSuccess?: (data: BarryIndividualConversation) => void,
) => {
	const { barryMessenger } = useFlags();

	return useQuery<unknown, ApiError, BarryIndividualConversation>({
		queryKey: barryConversationQueryKey(conversationUuid),
		queryFn: async () => {
			if (!conversationUuid || !barryMessenger) {
				return null;
			}

			const { data } = await getIndividualConversation(conversationUuid);
			return data as BarryIndividualConversation;
		},
		onSuccess,
		onError: (error) => {
			callErrorToast({
				content: getApiErrorText(
					error,
					'An error occurred while fetching your conversation with Barry',
				),
			});
			return error;
		},
		enabled,
	});
};
